import React, { useEffect, useState } from "react";
import Card from "./card";
import accountService from "../../../api/services/accountService";
import * as moment from "moment";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../common/styles/datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { playSong } from "../../common/actions/common.actions";
import { selectLoginData } from "../login/login.actions";
import { FiSearch } from "react-icons/fi";
import { BiSolidCalendar } from "react-icons/bi";
import { IoIosArrowBack } from "react-icons/io";

const Account = ({ setIsLoading }) => {
  const [transactionData, setTransactionData] = useState([]);
  const today = new Date();
  const [dateRange, setDateRange] = useState([today, today]);
  const [startDate, endDate] = dateRange;
  const [transactionsAvailable, setTransactionsAvailable] = useState(true);
  const { userDetails } = useSelector(selectLoginData);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSongClick = async () => {
    dispatch(playSong());
  };

  const handleClick = () => {
    handleSongClick();
    navigate("/dashboard");
  };

  const handleDateChange = (update) => {
    setDateRange(update);
  };

  const handleSearchClick = () => {
    fetchData(startDate, endDate);
  }

  const formatAmount = (value) => {
    const numericValue = Number(value || 0);
    return Number.isFinite(numericValue) ? numericValue.toLocaleString() : value;
  };

  const fetchData = async (start, end) => {
    if (start && end && userDetails?.userId) {
      setIsLoading?.(true);
      const formattedStartDate = moment(start).format("YYYY-MM-DD");
      const formattedEndDate = moment(end).format("YYYY-MM-DD");
      try {
        const response = await accountService.getTransactionsForUser({
          userId: Number(userDetails.userId),
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        });
        const data = await response.json();

        if (data?.userTransactions) {
          const filteredData = data?.userTransactions.filter(
            (transaction) =>
              moment(transaction?.date).isSameOrAfter(start, "day") &&
              moment(transaction?.date).isSameOrBefore(end, "day")
          );
          setTransactionData(filteredData);
          setTransactionsAvailable(filteredData.length > 0);
        } else {
          setTransactionData([]);
          setTransactionsAvailable(false);
        }
      } catch (error) {
        console.error("API request error:", error);
        setTransactionData([]);
        setTransactionsAvailable(false);
      } finally {
        setIsLoading?.(false);
      }
    }
  };

  useEffect(() => {
    fetchData(today, today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-full h-[100dvh] flex flex-col font-dangrek overflow-hidden bg-slate-100 dark:bg-bg_header_dark">
      <div className="flex justify-between items-center text-center py-2 pr-5 bg-bg_header dark:bg-bg_header_dark border-b dark:border-b dark:border-border_dark/[0.7] shadow-sm">
        <div className="flex items-center">
          <button
            className="top-3 left-3 w-10 h-10 mr-3
                    rounded-full drop-shadow-lg flex justify-center items-center text-white text-4xl"
            onClick={handleClick}
          >
             <IoIosArrowBack size={25} className="text-white"/>
          </button>
          <h3 className="text-white">{t("statement")}</h3>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        <div className="mx-auto w-full max-w-xl">
          <div className="mt-2 rounded-lg bg-dashboard border border-slate-700 p-4 text-white shadow-lg">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase text-white/[0.65]">{t("statement")}</p>
                <h2 className="mt-1 text-xl">{userDetails?.username || "-"}</h2>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase text-white/[0.65]">{t("balance")}</p>
                <p className="mt-1 font-inter text-xl font-bold">
                  {formatAmount(userDetails?.balance)} {t("ks")}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end my-2">
            <label
              htmlFor="accountDate"
              className="flex flex-row border border-slate-200 bg-white dark:border-white/[0.2] dark:bg-white/[0.15] py-2 px-3 rounded-md shadow-md justify-center items-center cursor-pointer transition hover:border-primary/[0.45] hover:shadow-lg"
            >
              <BiSolidCalendar size={19} className="text-primary dark:text-white mr-2" />
              <h3 className="text-primary dark:text-white/[0.85] font-myanmarSansPro">
                {moment(startDate).format("DD-MM-YYYY")} / {moment(endDate).format("DD-MM-YYYY")}
              </h3>
            </label>
            <DatePicker
              id="accountDate"
              selectsRange={true}
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateChange}
              dateFormat="yyyy-MM-dd"
              withPortal
              calendarClassName="sport-date-picker"
              className="rounded bg-white hidden"
            />
            <button
              type="button"
              className="flex items-center bg-white border border-slate-200 dark:border-white/[0.2] dark:bg-white/[0.15] ml-4 p-3 mr-2 rounded-md shadow-md cursor-pointer transition hover:border-primary/[0.45] hover:shadow-lg active:scale-95"
              onClick={handleSearchClick}
              aria-label="Search statement by date"
            >
              <FiSearch size={18} className="dark:text-white" />
            </button>
          </div>

          {transactionsAvailable ? (
            <div className="space-y-3">
              {transactionData?.map((data, index) => (
                <Card data={data} key={index} />
              ))}
            </div>
          ) : (
            <div className="mt-4 flex min-h-52 flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white px-6 py-6 text-center shadow-sm dark:border-white/[0.2] dark:bg-white/[0.08]">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-primary dark:bg-white/[0.12] dark:text-white">
                <FiSearch size={22} />
              </div>
              <p className="text-sm text-slate-600 dark:text-white/[0.75]">{t("noTransaction")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Account;
